import type { RowInformation, TableInformation } from '../../../config/flavourConfig';
import type { AgricultureContentDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/ctp';
import type { CarDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/mvp';
import type {
    CoverableDTO,
    CoverageDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import type {
    PrivateContentDTO,
    YouthContentDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/content';
import type { CaravanDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/uvp';
import ViewHippoInformationModal from '../../../shared_components/ViewHippoInformationModal/ViewHippoInformationModal';
import { formatStringToMonetaryValueString } from '../../../utils/coverablesUtil';
import { UVP_CONTENT_COV } from '../../../constants/coverage';

const getInsuranceSumsShoppingBasketConfig = (
    coverable: CoverableDTO
): TableInformation | undefined => {
    switch (coverable.flavourName) {
        case 'Indboforsikring til unge':
            return getYouthContentInsuranceSumsCoverableDetailsConfig(coverable);
        case 'Indboforsikring':
            return getPrivateContentInsuranceSumsCoverableDetailsConfig(coverable);
        case 'Privat løsøre- og ansvarsforsikring':
            return getAgricultureContentInsuranceSumsCoverableDetailsConfig(coverable);
        default:
            return undefined;
    }
};

const getYouthContentInsuranceSumsCoverableDetailsConfig = (
    youthCoverable: YouthContentDTO
): TableInformation => {
    return {
        title: 'Forsikringssummer',
        rowsInformation: [
            {
                name: 'Indbo',
                fieldValue: youthCoverable.homeYouthContentSum,
                valueType: 'amount',
                labelType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: 'DSS-CON-YOUTH-HIGHLIGHTED-TEXT-MODAL-CONTENT-TITLE',
                        description: 'DSS-CON-YOUTH-HIGHLIGHTED-TEXT-MODAL-CONTENT-DESCRIPTION'
                    }
                }
            },
            {
                name: 'Guld, sølv, smykker mv.',
                fieldValue: youthCoverable.homeYouthValuables,
                valueType: 'amount',
                labelType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: 'DSS-CON-YOUTH-HIGHLIGHTED-TEXT-MODAL-GOLD-SILVER-JEWELRY-TITLE',
                        description:
                            'DSS-CON-YOUTH-HIGHLIGHTED-TEXT-MODAL-GOLD-SILVER-JEWELRY-DESCRIPTION'
                    }
                }
            },
            {
                name: 'Cykler (pr. cykel)',
                fieldValue: youthCoverable.bicycleSum,
                valueType: 'amount',
                hidden: !youthCoverable.bicycleSum,
                labelType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: 'DSS-CON-HIGHLIGHTED-TEXT-MODAL-BICYCLES-TITLE',
                        description: 'DSS-CON-HIGHLIGHTED-TEXT-MODAL-BICYCLES-DESCRIPTION'
                    }
                }
            }
        ]
    };
};

const getPrivateContentInsuranceSumsCoverableDetailsConfig = (
    contentCoverable: PrivateContentDTO
): TableInformation => {
    return {
        title: 'Forsikringssummer',
        rowsInformation: [
            {
                name: 'Guld, sølv og smykker mv.',
                fieldValue: contentCoverable.goldSilverJewelryProtection,
                valueType: 'amount',
                labelType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: 'DSS-CON-ADULT-HIGHLIGHTED-TEXT-MODAL-GOLD-SILVER-JEWELRY-TITLE',
                        description:
                            'DSS-CON-ADULT-HIGHLIGHTED-TEXT-MODAL-GOLD-SILVER-JEWELRY-DESCRIPTION'
                    }
                }
            },
            {
                name: 'Designermøbler og -lamper',
                fieldValue: contentCoverable.designerFurnitureSum,
                valueType: 'amount',
                labelType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: 'DSS-CON-ADULT-HIGHLIGHTED-TEXT-MODAL-FURNITURE-TITLE',
                        description: 'DSS-CON-ADULT-HIGHLIGHTED-TEXT-MODAL-FURNITURE-DESCRIPTION'
                    }
                }
            },
            {
                name: 'Øvrigt indbo',
                fieldValue: contentCoverable.remainingContentSum,
                valueType: 'amount',
                hidden: !contentCoverable.remainingContentSum,
                labelType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: 'DSS-CON-ADULT-HIGHLIGHTED-TEXT-MODAL-OTHER-CONTENT-TITLE',
                        description:
                            'DSS-CON-ADULT-HIGHLIGHTED-TEXT-MODAL-OTHER-CONTENT-DESCRIPTION'
                    }
                }
            },
            {
                name: 'Forsikringssum i alt',
                fieldValue: contentCoverable.totalContentSum,
                valueType: 'amount',
                hidden: !contentCoverable.totalContentSum,
                labelType: 'bold'
            }
        ]
    };
};

const getAgricultureContentInsuranceSumsCoverableDetailsConfig = (
    coverable: AgricultureContentDTO
): TableInformation => {
    return {
        title: 'Forsikringssummer',
        rowsInformation: [
            {
                name: 'Guld, sølv, smykker, ure',
                fieldValue: coverable.goldSilverJewWatchesSum,
                valueType: 'amount'
            },
            {
                name: 'Designermøbler og -lamper',
                fieldValue: coverable.designerFurnitureSum,
                valueType: 'amount'
            },
            {
                name: 'Våben',
                fieldValue: coverable.weaponSum,
                valueType: 'amount'
            }
        ]
    };
};

const getPrivateCarInsuranceSumsCoverableDetailsConfig = (
    carCoverable: CarDTO
): TableInformation => {
    const selectedCoverages: CoverageDTO[] | undefined = carCoverable.coverages?.filter(
        (coverage) => coverage.selected
    );
    const rowsInformation: RowInformation[] = [];
    selectedCoverages?.forEach((selectedCoverage) => {
        if (
            selectedCoverage.publicID === 'MVPDriversAccident' ||
            selectedCoverage.publicID === 'MVPLeasingcarReturn'
        ) {
            selectedCoverage?.terms?.forEach((term) => {
                if (term.name !== 'Selvrisiko') {
                    rowsInformation.push({
                        name: term.name ?? '',
                        fieldValue: formatStringToMonetaryValueString(term.value ?? ''),
                        valueType: 'amount'
                    });
                }
            });
        }
    });
    return {
        title: 'Forsikringssummer',
        rowsInformation
    };
};

const getCaravanInsuranceSumsCoverableDetailsConfig = (
    caravanCoverable: CaravanDTO
): TableInformation => {
    let rowsInformation: RowInformation[] = [];
    const contentCoverage = caravanCoverable.coverages?.find(
        (coverage) => coverage.selected && coverage.publicID === UVP_CONTENT_COV
    );
    if (contentCoverage) {
        const sumTerms = contentCoverage.terms?.filter((term) => term.name === 'Sum');
        rowsInformation =
            sumTerms?.map((sumTerm) => ({
                name: 'Værdi af campingvognens indbo',
                fieldValue: formatStringToMonetaryValueString(sumTerm.value ?? ''),
                valueType: 'amount'
            })) ?? [];
    }
    return {
        title: 'Forsikringssummer',
        rowsInformation
    };
};

const roundUpManualSumToNearestThousand = (value: string): string => {
    return (Math.ceil(parseInt(value) / 1000) * 1000).toString();
};

export {
    getInsuranceSumsShoppingBasketConfig,
    getYouthContentInsuranceSumsCoverableDetailsConfig,
    getPrivateContentInsuranceSumsCoverableDetailsConfig,
    getPrivateCarInsuranceSumsCoverableDetailsConfig,
    getCaravanInsuranceSumsCoverableDetailsConfig,
    roundUpManualSumToNearestThousand
};
